import { useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCreatePolicy } from '@http/queries/useCreatePolicy';
import { useFetchPolicy } from '@http/queries/useFetchPolicy';
import LoadingSpinner from '@components/LoadingSpinner';
import PolicyForm, {
  blankPolicyInitialValues,
  policyToInitialValues,
  templateInitialValues,
} from './PolicyForm';
import type { PolicyTemplate } from './PolicyTemplateLibrary';
import { parsePolicyId } from './policyId';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { GovernanceAccessBoundary } from '../GovernanceAccessBoundary';

function NewPolicyContent() {
  const { t } = useInlineTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const createPolicy = useCreatePolicy();

  // When arriving from "Use template" in the library, the chosen template is
  // passed via router state and used to prefill the form.
  const template = (location.state as { template?: PolicyTemplate } | null)
    ?.template;

  // When arriving from "Duplicate" on a policy card, the source policy id is
  // passed via the query string and its values prefill the form.
  const duplicateId = parsePolicyId(searchParams.get('duplicateId'));
  const { data: duplicatedPolicy, isLoading: isDuplicateLoading } =
    useFetchPolicy(duplicateId);

  const initialValues = useMemo(() => {
    if (duplicatedPolicy) {
      const values = policyToInitialValues(duplicatedPolicy);
      return { ...values, name: t('copyName', { name: values.name }) };
    }
    if (template) return templateInitialValues(template, t);
    return blankPolicyInitialValues();
  }, [duplicatedPolicy, t, template]);

  // Wait for the source policy before rendering, so the form mounts prefilled.
  if (duplicateId != null && isDuplicateLoading) {
    return <LoadingSpinner />;
  }

  return (
    <PolicyForm
      heading={t('newPolicy')}
      description={t('newPolicyDescription')}
      submitLabel={t('createPolicy')}
      canSaveAsDraft
      isSubmitting={createPolicy.isPending}
      initialValues={initialValues}
      onSubmit={(body) =>
        createPolicy.mutate(body, {
          onSuccess: () => void navigate('/policies'),
        })
      }
    />
  );
}

function NewPolicy() {
  return (
    <GovernanceAccessBoundary area='customPolicies' requiredLevel='editor'>
      <NewPolicyContent />
    </GovernanceAccessBoundary>
  );
}

export default NewPolicy;
